import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { upperFirst } from 'lodash-es';

import { Badge, Button, Text } from '@change/ds-core-ui';
import { VictoryIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useNow } from '@change-corgi/core/react/date';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { Html } from 'src/app/shared/components/html';

type Props = {
	createdAt: string;
	description: string;
	newUpdate?: boolean;
};

export const VictoryUpdate = (({ ref, createdAt, description, newUpdate }) => {
	const now = useNow();
	const { translate, localizeRelativeTime } = useI18n();
	const pRef = useRef<HTMLParagraphElement>(null);
	const [clamped, setClamped] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const clampClass = 'line-clamp-2';

	const measure = () => {
		const el = pRef.current;
		if (!el) return;

		const isClamped = el.scrollHeight - el.clientHeight > 0;
		setClamped(isClamped);
	};

	useEffect(() => {
		const el = pRef.current;
		if (!el) return;
		if (expanded) {
			el.classList.remove(clampClass);
		} else {
			el.classList.add(clampClass);
		}

		measure();
	}, [expanded]);

	const victoryHeading = upperFirst(translate('corgi.petition.details.updates.confirmed_victory'));

	return (
		<div
			className="border-stroke-info-strong flex flex-row rounded-lg border-2 px-5 py-6"
			ref={ref}
			data-qa="petition-update-victory"
		>
			<div className="mr-3">
				<div className="bg-icon-accent-primary flex h-10 w-10 items-center justify-center rounded-full p-1">
					<VictoryIcon className="text-icon-inverse-strong block" size={21} />
				</div>
			</div>
			<div className="flex flex-col">
				<div className="flex flex-row">
					<Text as="div" variant="heading" size="x-small">
						{victoryHeading}
					</Text>
					{newUpdate && (
						<Badge
							className="ml-2 h-6"
							size="small"
							variant="information"
							emphasis="weaker"
							label={translate('corgi.petition.details.updates.badge_new_label')}
						></Badge>
					)}
				</div>

				<Text as="div" size="small" className="mb-2">
					{localizeRelativeTime(now, createdAt)}
				</Text>
				<Text as="div" ref={pRef} className={cn('italic', { [clampClass]: !expanded })}>
					<Html expressive html={description} breakWord />
				</Text>

				{clamped && (
					<Button
						variant="tertiary"
						size="small"
						className="mt-2 w-fit p-0"
						data-qa="petition-update-victory-read-full-response-button"
						onClick={() => {
							setExpanded(!expanded);
						}}
					>
						<Text className="text-typography-strong text-sm">
							<Translate value="corgi.petition.details.updates.read_full_response" />
						</Text>
					</Button>
				)}
			</div>
		</div>
	);
}) as React.FC<
	Props & {
		ref?: React.RefObject<HTMLDivElement>;
	}
>;
